import {
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import msalInstance from "./msalInstance";
import { tokenRequest } from "./msalConfig";

let inflight = null;

// MSAL error codes that mean silent auth genuinely cannot complete and the only
// way forward is a full interactive redirect (the hidden-iframe /authorize call
// couldn't finish — e.g. no live Entra session, blocked 3rd-party cookies, or a
// slow network). We treat these the same as InteractionRequiredAuthError.
const SILENT_UNRECOVERABLE_CODES = new Set([
  "timed_out",
  "monitor_window_timeout",
  "hash_empty_error",
  "empty_window_error",
]);

const needsInteraction = (error) =>
  error instanceof InteractionRequiredAuthError ||
  (error instanceof BrowserAuthError &&
    SILENT_UNRECOVERABLE_CODES.has(error.errorCode));

const isSso = () => sessionStorage.getItem("loginType") === "sso";

const getAccount = () =>
  msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;

/** Returns a valid id_token, refreshing it silently when expired. */
export const getIdToken = async ({ forceRefresh = false } = {}) => {
  if (!isSso()) return sessionStorage.getItem("jwtToken"); // CIAM path untouched

  const account = getAccount();
  if (!account) return null;

  if (inflight) return inflight; // de-dupe concurrent callers

  inflight = (async () => {
    try {
      const result = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account,
        forceRefresh,
      });
      const token = result.idToken;
      if (token) sessionStorage.setItem("jwtToken", token);
      return token;
    } catch (error) {
      if (needsInteraction(error)) {
        // Silent auth can't recover. If we still hold a cached token, keep using
        // it (the axios 401 handler / proactive refresh will retry later) rather
        // than yanking the user out of a working session. Only force a full
        // redirect when there is nothing to fall back on.
        const cached = sessionStorage.getItem("jwtToken");
        if (cached) return cached;
        try {
          await msalInstance.acquireTokenRedirect({ ...tokenRequest, account });
        } catch (redirectError) {
          console.error(
            "[tokenService] interactive redirect failed",
            redirectError,
          );
        }
        return null;
      }
      console.error("[tokenService] silent refresh failed", error);
      // Other transient errors: fall back to the cached token if present,
      // otherwise null so callers can route to the pre-login page.
      return sessionStorage.getItem("jwtToken") || null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

/** Seconds-since-epoch expiry of the cached id_token, or null. */
export const getIdTokenExpiry = () => getAccount()?.idTokenClaims?.exp ?? null;